/**
 * PlotlyChartCard — renders a saved Plotly chart in the dashboard grid.
 *
 * A "chart" `DashboardItem` stores:
 *   {
 *     item_type: 'chart',
 *     title:     string,
 *     sql_query: string,                 // run on every load
 *     chart_config: {
 *       data:    PlotlyData[],           // Plotly figure - data traces
 *       layout:  PlotlyLayout,           // figure layout
 *       frames:  PlotlyFrame[],          // optional animation frames
 *     }
 *   }
 *
 * The component executes ``sql_query`` on mount, hydrates the
 * column-named references inside ``chart_config.data`` from the result
 * rows, and renders the figure via ``react-plotly.js``.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Spin, Alert, Empty } from 'antd';
import Plot from 'react-plotly.js';
import { executeDashboardSQL } from '../services/dashboardService';
import { rowsToColumnar, hydrateFigure } from '../utils/plotlyData';
import { setChartData } from '../utils/chartDataCache';

export default function PlotlyChartCard({ item, isInteracting = false, sqlOverride = null }) {
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [columnar, setColumnar] = useState(null);

  const effectiveSql = sqlOverride || item?.sql_query;

  useEffect(() => {
    if (!effectiveSql) {
      setStatus('error');
      setError('Chart has no SQL query.');
      return undefined;
    }
    const ac = new AbortController();
    let cancelled = false;
    setStatus('loading');
    setError(null);
    (async () => {
      try {
        const resp = await executeDashboardSQL(effectiveSql, 5000, false, { signal: ac.signal });
        if (cancelled || ac.signal.aborted) return;
        const payload = resp?.data || resp;
        if (!payload?.columns || !payload?.rows) {
          throw new Error('Query returned no data.');
        }
        setChartData(item.id, { columns: payload.columns, rows: payload.rows });
        setColumnar(rowsToColumnar(payload.columns, payload.rows));
        setStatus('ready');
      } catch (err) {
        if (cancelled || ac.signal.aborted || err?.name === 'AbortError') return;
        setStatus('error');
        setError(err?.message || 'Failed to load chart data.');
      }
    })();
    return () => { cancelled = true; ac.abort(); };
  }, [item?.id, effectiveSql]);

  const figure = useMemo(() => {
    if (!columnar || !item?.chart_config) return null;
    return hydrateFigure(item.chart_config, columnar);
  }, [columnar, item?.chart_config]);

  if (status === 'loading') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Spin />
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div style={{ padding: 8 }}>
        <Alert type="error" showIcon message="Chart error" description={error} />
      </div>
    );
  }
  if (!figure || !figure.data?.length) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Empty description="No chart configured" />
      </div>
    );
  }

  // While the parent dashboard card is being dragged or resized, RGL
  // animates sibling sizes for ~200ms. With <Plot useResizeHandler /> the
  // chart redraws on every frame of that animation — multiplied across
  // every chart on the dashboard, that's the dominant cause of drag lag.
  //
  // Strategy: keep the Plot mounted (so its loaded SQL data + Plotly
  // graph state survive the gesture) but toggle the wrapper to
  // display:none during interaction. The element loses its layout box,
  // Plotly's internal ResizeObserver stops firing, and the chart
  // redraws exactly once when display returns to block at drag-end.
  // A static overlay shows in its place so the card isn't visually
  // empty while dragging.
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div style={{ width: '100%', height: '100%', display: isInteracting ? 'none' : 'block' }}>
        <Plot
          data={figure.data}
          layout={{
            ...figure.layout,
            // The dashboard card already shows the chart's title in its
            // header chrome — suppress Plotly's own title to avoid the
            // duplicate "Member Velocity …" appearing both in the card
            // header and over the plot area.
            title: undefined,
            autosize: true,
            // Tighter top margin since there's no title taking up space.
            margin: { t: 12, r: 16, b: 40, l: 48 },
          }}
          frames={figure.frames}
          config={{ displaylogo: false, responsive: true }}
          useResizeHandler
          style={{ width: '100%', height: '100%' }}
        />
      </div>
      {isInteracting && (
        <div
          className="dashboard-card-plot-frozen"
          style={{
            position: 'absolute', inset: 0,
            background: 'var(--theme-backgroundSecondary, #fafafa)',
            border: '1px dashed var(--theme-border, #d9d9d9)',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--theme-textSecondary, #999)',
            fontSize: 12,
          }}
        >
          chart paused while dragging…
        </div>
      )}
    </div>
  );
}
